import { useEffect, useRef, useState } from 'react';
import { ClippedImage } from './ClippedImage';
import { HomeMastheadTitle } from './HomeMastheadTitle';
import { motion, useTransform, useMotionValue, animate } from 'motion/react';

/**
 * A responsive masthead component that displays a clipped image or title
 * depending on the route.
 *
 * @param {string} image - The image to display in the masthead.
 * @param {string} alt - The alt text for the image.
 * @param {string} clipPaths - The clip paths for the image.
 * @param {number} transitionDuration - The duration of the transition animation.
 * @param {number} transitionDelay - The delay of the transition animation.
 * @param {string} transitionEase - The easing function for the transition animation.
 * @param {boolean} isExpanded - Whether the masthead is expanded or not.
 * @param {Object} dimensions - The dimensions of the image.
 * @param {string} titleAlignment - The alignment of the title in the masthead.
 */
export const Masthead = ({ image, alt, clipPaths, transitionDuration, transitionDelay, transitionEase, isExpanded, dimensions, routeName, titleAlignment }) => {
	const containerRef = useRef(null);
	const [isFullyExpanded, setIsFullyExpanded] = useState(routeName === 'Empathy');
	const scrollProgress = useMotionValue(routeName === 'Empathy' ? 1 : 0);
	const isAnimating = useRef(false);
	const hasStartedExpanding = useRef(false);
	const aspectRatio = dimensions ? dimensions.width / dimensions.height : 1.5;
	const scale = useTransform(scrollProgress, [0, 1], [1, Math.max(window.innerWidth / (((64 * window.innerHeight) / 100) * aspectRatio), window.innerHeight / ((64 * window.innerHeight) / 100))], {
		clamp: true,
	});

	useEffect(() => {
		/**
		 * Handles the wheel event to trigger the expansion animation.
		 *
		 * @param {WheelEvent} e - The wheel event object.
		 */
		const handleWheel = (e) => {
			// Check if the masthead is not fully expanded and not animating
			// and the scroll direction is downwards
			if (!isFullyExpanded && !isAnimating.current && !hasStartedExpanding.current && e.deltaY > 0) {
				// Start the expansion animation
				hasStartedExpanding.current = true;
				isAnimating.current = true;

				animate(scrollProgress, 1, {
					duration: 1,
					ease: [0.33, 1, 0.68, 1], // Custom easing for smooth animation
					onComplete: () => {
						// Set the state to fully expanded and reset animation flags
						setIsFullyExpanded(true);
						isAnimating.current = false;
						hasStartedExpanding.current = false;
					},
				});
			}
		};

		window.addEventListener('wheel', handleWheel, { passive: true });

		return () => {
			window.removeEventListener('wheel', handleWheel);
		};
	}, [isFullyExpanded, scrollProgress]);

	return (
		<div id="masthead" ref={containerRef} className="w-full overflow-hidden bg-white relative grid place-items-center z-0 [grid-template-areas:'stack'] masthead">
			<motion.div
				id="masthead-frame"
				style={{
					scale,
					width: `min(100%, calc(64svh * ${aspectRatio}))`,
				}}
				className="relative h-auto md:h-[64svh] flex items-center justify-center overflow-hidden bg-white origin-center [grid-area:stack] masthead-frame"
			>
				<ClippedImage src={image} alt={alt} clipPaths={clipPaths} transitionDuration={transitionDuration} transitionDelay={transitionDelay} transitionEase={transitionEase} isExpanded={isExpanded || scale.get() > 1} dimensions={dimensions} className="masthead-clipped-image" />
			</motion.div>

			{routeName === 'Empathy' ? (
				<HomeMastheadTitle />
			) : (
				<div id="masthead-title-frame" className="[grid-area:stack] w-full h-full text-white mix-blend-difference relative z-[1]">
					<div id="masthead-title" className={`container grid h-full max-w-container-lg ${titleAlignment}`}>
						<h1 className="font-trade-compressed uppercase m-0 text-[3.125rem] font-bold tracking-widest leading-[1]">{routeName}</h1>
					</div>
				</div>
			)}
		</div>
	);
};
